import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Coins, Robot, CalendarBlank, ChartBar } from "@phosphor-icons/react";

const eur = (v) => (v == null ? "—" : `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const usd = (v) => (v == null ? "—" : `$ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const AGENT_LABELS = {
    document_reader: "Lettura documenti",
    legal_risk: "Rischio legale",
    urban_planning: "Urbanistica",
    market_valuation: "Valutazione mercato",
    investment: "Analisi investimento",
    action_planner: "Piano operativo",
    report: "Report finale",
    ai_judge: "AI Judge (QA)",
    unknown: "Altro",
};

function KpiCard({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Icon size={16} weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
            </div>
            <div className="font-display-black text-2xl tabular-nums">{value}</div>
            {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function AiCosts() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get("/admin/costs/summary", { params: { days: 30 } })
            .then(({ data }) => setData(data))
            .catch(() => setErr(true));
    }, []);

    if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

    const daily = (data?.daily || []).map((d) => ({
        ...d,
        label: d.date.slice(8) + "/" + d.date.slice(5, 7),
        eur: Math.round(d.cost_usd * (data?.pricing?.eur_per_usd || 0.92) * 100) / 100,
    }));

    return (
        <Layout>
            <div className="space-y-6" data-testid="admin-ai-costs">
                <div>
                    <h1 className="font-display-black text-3xl flex items-center gap-2">
                        <Coins size={26} weight="fill" /> Costi AI
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Spesa giornaliera per le chiamate al modello AI (GPT-4o) della pipeline di analisi.
                        Valori stimati dai token effettivi di ogni chiamata — tariffe: $2,50/M input · $10/M output.
                    </p>
                </div>

                {err && (
                    <p className="text-sm text-rose-600" data-testid="costs-error">
                        Impossibile caricare i dati dei costi.
                    </p>
                )}

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="costs-kpi">
                            <KpiCard icon={CalendarBlank} label="Spesa oggi"
                                     value={eur(data.today.cost_eur)}
                                     sub={`${data.today.calls} chiamate · ${usd(data.today.cost_usd)}`}
                                     testid="costs-kpi-today" />
                            <KpiCard icon={Coins} label="Mese corrente"
                                     value={eur(data.month_to_date.cost_eur)}
                                     sub={`${data.month_to_date.calls} chiamate · ${data.month_to_date.analyses} analisi`}
                                     testid="costs-kpi-month" />
                            <KpiCard icon={Robot} label="Costo medio / analisi"
                                     value={eur(data.month_to_date.avg_per_analysis_eur)}
                                     sub={data.month_to_date.avg_per_analysis_usd ? usd(data.month_to_date.avg_per_analysis_usd) : "nessuna analisi questo mese"}
                                     testid="costs-kpi-avg" />
                            <KpiCard icon={ChartBar} label="Ultimi 30 giorni"
                                     value={eur(data.window.cost_eur)}
                                     sub={`${data.window.calls} chiamate · ${((data.window.input_tokens + data.window.output_tokens) / 1000).toFixed(0)}k token`}
                                     testid="costs-kpi-window" />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="costs-daily-chart">
                            <h3 className="font-display-black text-base mb-4">Spesa giornaliera (€) — ultimi 30 giorni</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" />
                                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip
                                            formatter={(v, name) => (name === "eur" ? [eur(v), "Spesa"] : [v, "Chiamate"])}
                                            labelFormatter={(l) => `Giorno ${l}`}
                                            contentStyle={{ background: "#141C33", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                                        />
                                        <Bar dataKey="eur" fill="#3B66F5" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-x-auto" data-testid="costs-by-agent">
                            <div className="p-5 pb-0">
                                <h3 className="font-display-black text-base">Ripartizione per agente AI (30 giorni)</h3>
                            </div>
                            <table className="w-full text-sm mt-3">
                                <thead>
                                    <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                        <th className="px-5 py-2">Agente</th>
                                        <th className="px-3 py-2">Chiamate</th>
                                        <th className="px-3 py-2">Spesa (€)</th>
                                        <th className="px-3 py-2">Spesa ($)</th>
                                        <th className="px-5 py-2">% del totale</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.by_agent.map((a) => (
                                        <tr key={a.agent} className="border-b border-gray-50" data-testid={`costs-agent-row-${a.agent}`}>
                                            <td className="px-5 py-2.5 font-medium">{AGENT_LABELS[a.agent] || a.agent}</td>
                                            <td className="px-3 py-2.5 tabular-nums">{a.calls}</td>
                                            <td className="px-3 py-2.5 tabular-nums font-semibold">{eur(a.cost_eur)}</td>
                                            <td className="px-3 py-2.5 tabular-nums text-gray-500">{usd(a.cost_usd)}</td>
                                            <td className="px-5 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-1.5 w-28 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-[#3B66F5] rounded-full"
                                                             style={{ width: `${data.window.cost_usd ? Math.round((a.cost_usd / data.window.cost_usd) * 100) : 0}%` }} />
                                                    </div>
                                                    <span className="text-xs tabular-nums text-gray-500">
                                                        {data.window.cost_usd ? Math.round((a.cost_usd / data.window.cost_usd) * 100) : 0}%
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {data.by_agent.length === 0 && (
                                        <tr><td colSpan={5} className="px-5 py-6 text-center text-sm text-gray-500" data-testid="costs-empty">
                                            Nessuna chiamata AI registrata ancora — i dati compariranno dalla prossima analisi eseguita.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <p className="text-[11px] text-gray-400">
                            I costi sono stime calcolate sui token di ogni chiamata reale (≈4 caratteri/token) alle tariffe di listino GPT-4o.
                            Il saldo effettivo della Universal Key è visibile su Emergent → Profilo → Manage plan.
                        </p>
                    </>
                )}
            </div>
        </Layout>
    );
}
